/**
 * Real-time Discount Calculator Component (Story 7.4)
 *
 * Shows discount calculation preview based on coupon type and purchase amount.
 * Updates in real-time as the user types.
 */

import React from 'react';
import { Calculator, AlertTriangle, CheckCircle } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { formatMoney } from '@zlinkx/shared-v2';
import type { CheckCouponCodeResponseType } from '@zlinkx/shared-v2';

type ValidatedCoupon = NonNullable<CheckCouponCodeResponseType['data']['coupon']>;

interface DiscountCalculatorProps {
	coupon: ValidatedCoupon;
	purchaseAmountCents: number; // Amount in cents for calculation
}

export const DiscountCalculator: React.FC<DiscountCalculatorProps> = ({
	coupon,
	purchaseAmountCents,
}) => {
	const { t } = useTranslation();

	// Calculate discount based on coupon type
	const calculateDiscount = (): {
		discountCents: number;
		discountDisplay: string;
		isFreeItem: boolean;
	} => {
		const { couponType, discountValue, itemDescription } = coupon;

		switch (couponType) {
				case 'percentage': {
					const percentDiscount = Math.floor(
						purchaseAmountCents * ((discountValue ?? 0) / 100)
					);
					return {
						discountCents: percentDiscount,
						discountDisplay: t('merchant.redemption.discount.percentage', {
							amount: formatMoney(percentDiscount),
							percent: discountValue ?? 0,
						}),
						isFreeItem: false,
					};
				}

			case 'fixed_amount': {
				// Fixed discount is stored in cents in discountValue
				// Cannot exceed purchase amount
				const fixedDiscount = Math.min(discountValue ?? 0, purchaseAmountCents);
				return {
					discountCents: fixedDiscount,
					discountDisplay: formatMoney(fixedDiscount),
					isFreeItem: false,
				};
			}

			case 'free_item':
				return {
					discountCents: 0,
					discountDisplay: t('merchant.redemption.discount.freeItem', {
						item: itemDescription ?? '',
					}),
					isFreeItem: true,
				};

			case 'bogo':
				return {
					discountCents: 0,
					discountDisplay: t('merchant.redemption.discount.bogo', {
						item: itemDescription ?? '',
					}),
					isFreeItem: true,
				};

			default:
				return { discountCents: 0, discountDisplay: '-', isFreeItem: false };
		}
	};

	const { discountCents, discountDisplay, isFreeItem } = calculateDiscount();
	const finalAmountCents = purchaseAmountCents - discountCents;

	// Check minimum purchase requirement
	// minPurchase is in DOLLARS in the schema, convert to cents for comparison
	const minPurchaseCents = (coupon.minPurchase ?? 0) * 100;
	const isBelowMinimum =
		minPurchaseCents > 0 && purchaseAmountCents < minPurchaseCents;
	const hasAmount = purchaseAmountCents > 0;

	return (
		<div
			className={`rounded-lg p-4 ${isBelowMinimum ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-200'}`}
		>
			<div className='flex items-center gap-2 mb-3'>
				<Calculator className='w-5 h-5 text-gray-500' />
				<h4 className='font-medium text-gray-900'>
					{t('merchant.redemption.discount.calculatorTitle')}
				</h4>
			</div>

			{hasAmount ? (
				<div className='space-y-2'>
					{/* Original Amount */}
					<div className='flex justify-between text-sm'>
						<span className='text-gray-600'>
							{t('merchant.redemption.discount.originalAmount')}
						</span>
						<span className='text-gray-900'>
							{formatMoney(purchaseAmountCents)}
						</span>
					</div>

					{/* Discount */}
					<div className='flex justify-between text-sm'>
						<span className='text-gray-600'>
							{isFreeItem
								? t('merchant.redemption.discount.freeItemLabel')
								: t('merchant.redemption.discount.discountLabel')}
						</span>
						<span className='text-green-600'>
							{isFreeItem ? discountDisplay : `-${discountDisplay}`}
						</span>
					</div>

					{/* Divider */}
					<div className='border-t border-gray-300 my-2' />

					{/* Final Amount */}
					<div className='flex justify-between'>
						<span className='font-medium text-gray-900'>
							{t('merchant.redemption.discount.finalAmount')}
						</span>
						<span className='font-bold text-lg text-gray-900'>
							{formatMoney(finalAmountCents)}
						</span>
					</div>

					{/* Free Item Note */}
					{isFreeItem && (
						<div className='flex items-center gap-2 mt-2 p-2 bg-green-50 rounded-md'>
							<CheckCircle className='w-4 h-4 text-green-600' />
							<span className='text-sm text-green-700'>{discountDisplay}</span>
						</div>
					)}

					{/* Minimum Purchase Warning */}
					{isBelowMinimum && (
						<div className='flex items-center gap-2 mt-2 p-2 bg-amber-100 rounded-md'>
							<AlertTriangle className='w-4 h-4 text-amber-600' />
							<span className='text-sm text-amber-700'>
								{t('merchant.redemption.discount.minPurchaseError', {
									amount: formatMoney(minPurchaseCents),
								})}
							</span>
						</div>
					)}
				</div>
			) : (
				<p className='text-sm text-gray-500 text-center py-4'>
					{t('merchant.redemption.discount.enterAmount')}
				</p>
			)}
		</div>
	);
};

export default DiscountCalculator;
